import { FC, ReactNode, SyntheticEvent, Dispatch, SetStateAction, useState, useEffect } from 'react'
import { Box, CircularProgress, Tab, Typography } from '@mui/material'

/* Utils */
import { IntelInsight } from '../../../../../models'
import { getMissingSourcesMap, sidePanelWidth } from '../../utils'

/* Components */
import { IntelFeedIcon } from '../../../../components/svg'
import {
  FeedSectionWrapper,
  IntelFeedTabPanel,
  IntelFeedTabs,
  IntelFeedWrapper,
  LoadingWrapper
} from '../../dashboard.styles'
import IntelFeedCard from '../../components/IntelFeedCard'
import NoFeedFound from './NoFeedFound'

interface TabPanelProps {
  children?: ReactNode
  index: number
  value: number
  feedHeight?: number
  onScroll: (e: any) => void
}

interface Props {
  id?: string
  feedHeight: number
  intelFeedTabValue: number
  handleTabChange: (event: SyntheticEvent, newValue: number) => void
  scrollHandler: (e: any) => void
  feedLoading: boolean
  internalFeed: IntelInsight[]
  fetchBookmarkFeed: () => void
  setDeleting: Dispatch<SetStateAction<boolean>>
  bookmarkFeed: IntelInsight[]
  deleting: boolean
  loading: boolean
}

const FeedSection: FC<Props> = ({
  id,
  intelFeedTabValue,
  handleTabChange,
  // feedHeight,
  scrollHandler,
  feedLoading,
  internalFeed,
  fetchBookmarkFeed,
  setDeleting,
  bookmarkFeed,
  deleting,
  loading
}) => {
  const [missingImageSourcesMap, setMissingImageSourcesMap] = useState<Map<string, string>>(new Map())

  useEffect(() => {
    setMissingImageSourcesMap(getMissingSourcesMap())
  }, [internalFeed])

  return (
    <FeedSectionWrapper id={id} className="feed-section-wrapper">
      <IntelFeedWrapper width={sidePanelWidth} className="intel-feed-wrapper">
        <Box className="header">
          <Box className="header-title">
            <IntelFeedIcon />
            <Typography>Intelligence Feed</Typography>
          </Box>

          <IntelFeedTabs
            value={intelFeedTabValue}
            onChange={handleTabChange}
            aria-label="intel feed tabs"
            TabIndicatorProps={{
              style: { display: 'none' }
            }}
          >
            <Tab label="Explore" {...a11yProps(0)} />
            <Tab label="Saved" {...a11yProps(1)} />
          </IntelFeedTabs>
        </Box>

        <TabPanel value={intelFeedTabValue} index={0} onScroll={scrollHandler}>
          {feedLoading && (
            <LoadingWrapper>
              <CircularProgress size="40px" color="primary" />
            </LoadingWrapper>
          )}
          {!feedLoading && !internalFeed.length && <NoFeedFound />}
          {!feedLoading &&
            internalFeed.length &&
            internalFeed.map((n, index) => (
              <IntelFeedCard
                key={index}
                intelInsight={n}
                callback={fetchBookmarkFeed}
                setDeleting={setDeleting}
                missingImageSourcesMap={missingImageSourcesMap}
              />
            ))}
          {!feedLoading && loading && (
            <LoadingWrapper>
              <CircularProgress size="40px" color="primary" />
            </LoadingWrapper>
          )}
        </TabPanel>
        <TabPanel value={intelFeedTabValue} index={1} onScroll={scrollHandler}>
          {feedLoading && (
            <LoadingWrapper>
              <CircularProgress size="40px" color="primary" />
            </LoadingWrapper>
          )}
          {!feedLoading && !bookmarkFeed.length ? (
            <NoFeedFound />
          ) : (
            !feedLoading &&
            bookmarkFeed.length &&
            bookmarkFeed.map((n, index) => (
              <IntelFeedCard
                key={index}
                intelInsight={n}
                callback={fetchBookmarkFeed}
                setDeleting={setDeleting}
                missingImageSourcesMap={missingImageSourcesMap}
              />
            ))
          )}

          {!feedLoading && deleting && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <CircularProgress size="40px" color="primary" />
            </Box>
          )}
        </TabPanel>
      </IntelFeedWrapper>
    </FeedSectionWrapper>
  )
}

function a11yProps(index: number) {
  return {
    id: `intel-feed-tab-${index}`,
    'aria-controls': `intel-feed-tabpanel-${index}`
  }
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, onScroll, ...other } = props

  return (
    <IntelFeedTabPanel
      style={value !== index ? { padding: 0, height: 0, display: 'none' } : {}}
      role="tabpanel"
      hidden={value !== index}
      id={`intel-feed-tabpanel-${index}`}
      aria-labelledby={`intel-feed-tab-${index}`}
      onScroll={onScroll}
      {...other}
    >
      {value === index && children}
    </IntelFeedTabPanel>
  )
}

export default FeedSection
